import { FunctionComponent, memo, useMemo, useState } from "react";
import { ICombinedMessagesAndIssues, IProps } from "./writing-issues-table.types";
import {
    Box,
    BoxContent,
    BoxHeader,
    BoxHeading,
    Cell,
    Column,
    FormattedDateTime,
    Heading,
    None,
    Table,
    TableColumns,
} from "@edgetier/client-components";
import { useAllInteractionsMessages } from "hooks-for/interactions/use-interactions-messages";
import { useAllWritingIssues } from "hooks-for/writing-issues/use-writing-issues";
import { useInteractionsMessageQueryParameters } from "hooks-for/interaction-messages/use-interactions-messages-query-parameters";
import { combineData } from "./writing-issues-table.utilities";
import { WritingIssueType } from "@edgetier/watchtower-types";
import WritingIssueTableMenu from "../writing-issue-table-menu";
import { WRITING_ISSUES_NO_PREFIX_FILTERS } from "../writing-issues.constants";
import InteractionModal from "components-for/interactions/interaction-modal";
import { useModal } from "@edgetier/modal";
import "./writing-issues-table.scss";
import { Markdown } from "@edgetier/components";

/**
 * A table showing the individual writing issues.
 * @param props.parameters Optional parameters that will be passed in the request to get the table data.
 * @returns Table of writing issues.
 */
const WritingIssuesTable: FunctionComponent<IProps> = ({ parameters = {}, requestsEnabled = true }) => {
    const [visibleInteractionId, setVisibleInteractionId] = useState<string | null>(null);
    const { openModal, modalProps } = useModal({ onCloseModal: () => setVisibleInteractionId(null) });

    const queryParameters = useInteractionsMessageQueryParameters({
        additionalParameters: parameters,
        prefixValue: "interaction",
        excludedParameters: WRITING_ISSUES_NO_PREFIX_FILTERS,
    });
    const { data: messages } = useAllInteractionsMessages(queryParameters, {
        enabled: requestsEnabled,
    });
    const { data: writingIssues } = useAllWritingIssues(queryParameters, {
        enabled: requestsEnabled,
    });

    const [issueTypeFilter, setIssueTypeFilter] = useState<WritingIssueType | undefined>();
    const filteredWritingIssues = useMemo(() => {
        if (typeof issueTypeFilter !== "undefined") {
            return writingIssues?.filter(({ writingIssueType }) => writingIssueType === issueTypeFilter);
        }
        return writingIssues;
    }, [issueTypeFilter, writingIssues]);

    const data = combineData(messages, filteredWritingIssues);

    return (
        <>
            {visibleInteractionId !== null && (
                <InteractionModal areWritingIssuesVisible={true} interactionId={visibleInteractionId} {...modalProps} />
            )}

            <Box className="writing-issues-table">
                <BoxHeader>
                    <BoxHeading>
                        <div className="writing-issues-table__heading">
                            Issues
                            {requestsEnabled ? (
                                ""
                            ) : (
                                <span className="writing-issues-table__heading-info">
                                    (Click on issue to see examples)
                                </span>
                            )}
                        </div>
                    </BoxHeading>
                </BoxHeader>
                <BoxContent horizontalPadding={false}>
                    <div className="writing-issues-table__container">
                        <Table<ICombinedMessagesAndIssues>
                            rows={requestsEnabled ? data : []}
                            getRowKey={({ issue }) => issue}
                            isInBox
                            headerIsSticky
                            onRowClick={({ interactionId }) => {
                                setVisibleInteractionId(interactionId ?? null);
                                openModal();
                            }}
                        >
                            <TableColumns>
                                <Column>
                                    <Heading>Time</Heading>

                                    <Cell<ICombinedMessagesAndIssues>>
                                        {({ time }) =>
                                            time instanceof Date ? (
                                                <div className="writing-issues-table__time">
                                                    <FormattedDateTime dateTime={time} />
                                                </div>
                                            ) : (
                                                <None />
                                            )
                                        }
                                    </Cell>
                                </Column>
                                <Column>
                                    <Heading>Message</Heading>
                                    <Cell<ICombinedMessagesAndIssues>>
                                        {({ message }) =>
                                            typeof message === "undefined" ? (
                                                <None />
                                            ) : (
                                                <Markdown markdownString={message} />
                                            )
                                        }
                                    </Cell>
                                </Column>
                                <Column>
                                    <Heading
                                        isFiltered={typeof issueTypeFilter !== "undefined"}
                                        menu={
                                            <WritingIssueTableMenu
                                                value={issueTypeFilter}
                                                onClick={(value) =>
                                                    setIssueTypeFilter((oldValue) =>
                                                        oldValue === value ? undefined : value
                                                    )
                                                }
                                            />
                                        }
                                    >
                                        Issue Type
                                    </Heading>
                                    <Cell<ICombinedMessagesAndIssues>>
                                        {({ writingIssueType }) => writingIssueType}
                                    </Cell>
                                </Column>
                                <Column>
                                    <Heading>Issue</Heading>
                                    <Cell<ICombinedMessagesAndIssues>>{({ issue }) => issue}</Cell>
                                </Column>
                            </TableColumns>
                        </Table>
                    </div>
                </BoxContent>
            </Box>
        </>
    );
};

export default memo(WritingIssuesTable);
